import React, { useState } from 'react';
import { TrendingUp, DollarSign, Users, Calendar, BarChart3, LineChart, PieChart, Target } from 'lucide-react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const Perfomancly = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Données pour les graphiques de comparaison sur 5 ans
    const performanceData = [
        { annee: '2020', ca: 850000, clients: 45, marge: 320000, ebe: 180000, resultat: 95000 },
        { annee: '2021', ca: 920000, clients: 52, marge: 365000, ebe: 210000, resultat: 125000 },
        { annee: '2022', ca: 1100000, clients: 58, marge: 445000, ebe: 285000, resultat: 180000 },
        { annee: '2023', ca: 1250000, clients: 65, marge: 520000, ebe: 340000, resultat: 220000 },
        { annee: '2024', ca: 1380000, clients: 72, marge: 580000, ebe: 385000, resultat: 265000 }
    ];

    const rentabiliteData = [
        { id: 'Marge Brute', value: 42, color: '#10b981' },
        { id: 'EBE', value: 28, color: '#3b82f6' },
        { id: 'Résultat Net', value: 19, color: '#8b5cf6' },
        { id: 'Autres', value: 11, color: '#f59e0b' }
    ];

    // Données actuelles 2024
    const currentYear = {
        ca: 1380000,
        nombreClients: 72,
        caMoyenClient: 19167,
        margeBrute: 580000,
        ebe: 385000,
        resultatExploitation: 320000,
        resultatNet: 265000,
        seuilRentabilite: {
            montant: 890000,
            dateAtteinte: '2024-08-15'
        }
    };

    // Préparation des données pour Nivo
    const lineData = [
        {
            id: "Chiffre d'Affaires",
            color: "#10b981",
            data: performanceData.map(d => ({ x: d.annee, y: d.ca }))
        },
        {
            id: "Résultat Net",
            color: "#dc3545",
            data: performanceData.map(d => ({ x: d.annee, y: d.resultat }))
        },
        {
            id: "EBE",
            color: "#3b82f6",
            data: performanceData.map(d => ({ x: d.annee, y: d.ebe }))
        }
    ];

    const barData = performanceData.map(d => ({
        annee: d.annee,
        clients: d.clients
    }));

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculatePercentage = (current, previous) => {
        if (!previous) return 0;
        return ((current - previous) / previous * 100).toFixed(1);
    };

    return (
        <>
            <div className="app-container">
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

                <div className="main-wrapper">
                    <Navbar onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

                    <main className="main-content">
                        {/* Header Section */}
                        <div className="page-header">
                            <div className="header-content">
                                <div className="header-text">
                                    <h1 className="page-title">Performances & Rentabilité</h1>
                                    <p className="page-subtitle">Analyse détaillée des performances financières de votre entreprise</p>
                                </div>
                            </div>
                        </div>

                        {/* KPIs Cards */}
                        <div className="kpis-grid">
                            <div className="kpi-card primary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Chiffre d'Affaires Total</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.ca)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.ca, performanceData[3].ca)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <DollarSign size={36} />
                                    </div>
                                </div>
                            </div>

                            <div className="kpi-card success">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Nombre de Clients</h3>
                                        <p className="kpi-value">{currentYear.nombreClients}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.nombreClients, performanceData[3].clients)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Users size={36} />
                                    </div>
                                </div>
                            </div>

                            <div className="kpi-card info">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">CA Moyen/Client</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.caMoyenClient)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.caMoyenClient, performanceData[3].ca / performanceData[3].clients)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Target size={36} />
                                    </div>
                                </div>
                            </div>

                            <div className="kpi-card warning">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Marge Brute</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.margeBrute)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.margeBrute, performanceData[3].marge)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingUp size={36} />
                                    </div>
                                </div>
                            </div>

                            <div className="kpi-card secondary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">EBE</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.ebe)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.ebe, performanceData[3].ebe)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <BarChart3 size={36} />
                                    </div>
                                </div>
                            </div>

                            <div className="kpi-card danger">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Résultat Net</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.resultatNet)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.resultatNet, performanceData[3].resultat)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <LineChart size={36} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Seuil de Rentabilité */}
                        <div className="seuil-section">
                            <div className="seuil-card">
                                <div className="seuil-header">
                                    <h3 className="seuil-title">Seuil de Rentabilité</h3>
                                    <Calendar size={20} className="seuil-icon" />
                                </div>
                                <div className="seuil-content">
                                    <div className="seuil-info">
                                        <p className="seuil-amount">
                                            Montant: <strong>{formatCurrency(currentYear.seuilRentabilite.montant)}</strong>
                                        </p>
                                        <p className="seuil-date">
                                            Date d'atteinte: <strong>{formatDate(currentYear.seuilRentabilite.dateAtteinte)}</strong>
                                        </p>
                                    </div>
                                    <div className="seuil-progress">
                                        <div className="progress-bar">
                                            <div className="progress-fill" style={{width: `${(currentYear.seuilRentabilite.montant / currentYear.ca) * 100}%`}}></div>
                                        </div>
                                        <span className="progress-text">
                                            {((currentYear.seuilRentabilite.montant / currentYear.ca) * 100).toFixed(1)}% du CA annuel
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="charts-section">
                            {/* Evolution CA et Résultats avec Nivo Line Chart */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution CA et Résultats (5 dernières années)</h3>
                                </div>
                                <div className="chart-content">
                                    <div style={{ height: '400px' }}>
                                        <ResponsiveLine
                                            data={lineData}
                                            margin={{ top: 50, right: 110, bottom: 50, left: 80 }}
                                            xScale={{ type: 'point' }}
                                            yScale={{
                                                type: 'linear',
                                                min: 'auto',
                                                max: 'auto',
                                                stacked: false,
                                                reverse: false
                                            }}
                                            yFormat=" >-.0f"
                                            curve="catmullRom"
                                            axisTop={null}
                                            axisRight={null}
                                            axisBottom={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: 'Années',
                                                legendOffset: 36,
                                                legendPosition: 'middle'
                                            }}
                                            axisLeft={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: 'Montant (XOF)',
                                                legendOffset: -60,
                                                legendPosition: 'middle',
                                                format: value => `${(value / 1000)}k`
                                            }}
                                            pointSize={8}
                                            pointColor={{ theme: 'background' }}
                                            pointBorderWidth={2}
                                            pointBorderColor={{ from: 'serieColor' }}
                                            pointLabel="y"
                                            pointLabelYOffset={-12}
                                            useMesh={true}
                                            legends={[
                                                {
                                                    anchor: 'bottom-right',
                                                    direction: 'column',
                                                    justify: false,
                                                    translateX: 100,
                                                    translateY: 0,
                                                    itemsSpacing: 0,
                                                    itemDirection: 'left-to-right',
                                                    itemWidth: 80,
                                                    itemHeight: 20,
                                                    itemOpacity: 0.75,
                                                    symbolSize: 12,
                                                    symbolShape: 'circle',
                                                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                                    effects: [
                                                        {
                                                            on: 'hover',
                                                            style: {
                                                                itemBackground: 'rgba(0, 0, 0, .03)',
                                                                itemOpacity: 1
                                                            }
                                                        }
                                                    ]
                                                }
                                            ]}
                                            tooltip={({ point }) => (
                                                <div style={{
                                                    background: 'white',
                                                    padding: '12px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <strong>{point.serieId}</strong><br/>
                                                    Année: {point.data.x}<br/>
                                                    Montant: {formatCurrency(point.data.y)}
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Evolution Clients avec Nivo Bar Chart */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution du Nombre de Clients</h3>
                                </div>
                                <div className="chart-content">
                                    <div style={{ height: '400px' }}>
                                        <ResponsiveBar
                                            data={barData}
                                            keys={['clients']}
                                            indexBy="annee"
                                            margin={{ top: 50, right: 60, bottom: 50, left: 60 }}
                                            padding={0.3}
                                            valueScale={{ type: 'linear' }}
                                            indexScale={{ type: 'band', round: true }}
                                            colors={['#10b981']}
                                            borderColor={{
                                                from: 'color',
                                                modifiers: [
                                                    [
                                                        'darker',
                                                        1.6
                                                    ]
                                                ]
                                            }}
                                            axisTop={null}
                                            axisRight={null}
                                            axisBottom={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: 'Années',
                                                legendPosition: 'middle',
                                                legendOffset: 32
                                            }}
                                            axisLeft={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: 'Nombre de clients',
                                                legendPosition: 'middle',
                                                legendOffset: -40
                                            }}
                                            labelSkipWidth={12}
                                            labelSkipHeight={12}
                                            labelTextColor={{
                                                from: 'color',
                                                modifiers: [
                                                    [
                                                        'darker',
                                                        1.6
                                                    ]
                                                ]
                                            }}
                                            tooltip={({ indexValue, value }) => (
                                                <div style={{
                                                    background: 'white',
                                                    padding: '12px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <strong>Année {indexValue}</strong><br/>
                                                    Clients: {value}
                                                </div>
                                            )}
                                            animate={true}
                                            motionStiffness={90}
                                            motionDamping={15}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Répartition Rentabilité avec Nivo Pie Chart */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition de la Rentabilité 2024</h3>
                                </div>
                                <div className="chart-content">
                                    <div style={{ height: '400px' }}>
                                        <ResponsivePie
                                            data={rentabiliteData}
                                            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                                            innerRadius={0.5}
                                            padAngle={0.7}
                                            cornerRadius={3}
                                            activeOuterRadiusOffset={8}
                                            colors={{ datum: 'data.color' }}
                                            borderWidth={1}
                                            borderColor={{
                                                from: 'color',
                                                modifiers: [
                                                    [
                                                        'darker',
                                                        0.2
                                                    ]
                                                ]
                                            }}
                                            arcLinkLabelsSkipAngle={10}
                                            arcLinkLabelsTextColor="#333333"
                                            arcLinkLabelsThickness={2}
                                            arcLinkLabelsColor={{ from: 'color' }}
                                            arcLabelsSkipAngle={10}
                                            arcLabelsTextColor={{
                                                from: 'color',
                                                modifiers: [
                                                    [
                                                        'darker',
                                                        2
                                                    ]
                                                ]
                                            }}
                                            legends={[
                                                {
                                                    anchor: 'bottom',
                                                    direction: 'row',
                                                    justify: false,
                                                    translateX: 0,
                                                    translateY: 56,
                                                    itemsSpacing: 0,
                                                    itemWidth: 100,
                                                    itemHeight: 18,
                                                    itemTextColor: '#999',
                                                    itemDirection: 'left-to-right',
                                                    itemOpacity: 1,
                                                    symbolSize: 12,
                                                    symbolShape: 'circle',
                                                    effects: [
                                                        {
                                                            on: 'hover',
                                                            style: {
                                                                itemTextColor: '#000'
                                                            }
                                                        }
                                                    ]
                                                }
                                            ]}
                                            tooltip={({ datum }) => (
                                                <div style={{
                                                    background: 'white',
                                                    padding: '12px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <strong>{datum.id}</strong><br/>
                                                    Pourcentage: {datum.value}%
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Commentaires du Cabinet */}
                        <div className="commentaires-section">
                            <div className="commentaires-card">
                                <div className="commentaires-header">
                                    <h3 className="commentaires-title">Commentaires du Cabinet CINEF-EC</h3>
                                    <div className="commentaires-date">
                                        Dernière mise à jour: {formatDate('2024-08-20')}
                                    </div>
                                </div>
                                <div className="commentaires-content">
                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Analyse des Performances</h4>
                                        <p className="commentaire-text">
                                            Excellente progression du chiffre d'affaires avec une croissance de {calculatePercentage(currentYear.ca, performanceData[3].ca)}% par rapport à 2023. 
                                            Cette performance s'appuie sur une stratégie d'acquisition client efficace (+{calculatePercentage(currentYear.nombreClients, performanceData[3].clients)}% de clients) 
                                            et une amélioration du CA moyen par client.
                                        </p>
                                    </div>
                                    
                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Rentabilité</h4>
                                        <p className="commentaire-text">
                                            La marge brute reste solide à 42% du CA, témoignant d'un bon contrôle des coûts directs. 
                                            L'EBE progresse significativement (+{calculatePercentage(currentYear.ebe, performanceData[3].ebe)}%), 
                                            traduisant une efficacité opérationnelle accrue.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Recommandations</h4>
                                        <p className="commentaire-text">
                                            Le seuil de rentabilité atteint dès août confirme la solidité du modèle économique. 
                                            Nous recommandons de poursuivre la stratégie de développement commercial tout en 
                                            maintenant la vigilance sur les coûts de structure.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .app-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .main-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 320px;
        }

        .main-content {
          flex: 1;
          padding: 32px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 138, 68, 0.3) transparent;
        }

        .main-content::-webkit-scrollbar {
          width: 6px;
        }

        .main-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .main-content::-webkit-scrollbar-thumb {
          background: rgba(0, 138, 68, 0.2);
          border-radius: 10px;
        }

        /* Header Section */
        .page-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-text {
          flex: 1;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 6px 0;
          background: linear-gradient(135deg, #dc3545, #008A44);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 16px;
          font-weight: 500;
        }

        /* KPIs Grid */
        .kpis-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .kpi-card {
          background: #ffffff;
          padding: 28px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .kpi-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: currentColor;
        }

        .kpi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
        }

        .kpi-card.primary { color: #dc3545; }
        .kpi-card.success { color: #10b981; }
        .kpi-card.info { color: #3b82f6; }
        .kpi-card.warning { color: #f59e0b; }
        .kpi-card.secondary { color: #8b5cf6; }
        .kpi-card.danger { color: #ef4444; }

        .kpi-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .kpi-info {
          flex: 1;
        }

        .kpi-title {
          font-size: 16px;
          font-weight: 600;
          color: #64748b;
          margin: 0 0 12px 0;
        }

        .kpi-value {
          font-size: 28px;
          font-weight: 700;
          color: currentColor;
          margin: 0 0 8px 0;
          line-height: 1;
        }

        .kpi-trend {
          font-size: 14px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 6px;
        }

        .kpi-trend.positive {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .kpi-icon {
          opacity: 0.8;
          color: currentColor;
        }

        /* Seuil Section */
        .seuil-section {
          margin-bottom: 32px;
        }

        .seuil-card {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .seuil-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 28px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(0, 138, 68, 0.05) 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .seuil-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .seuil-icon {
          color: #64748b;
        }

        .seuil-content {
          padding: 28px;
        }

        .seuil-info {
          display: flex;
          gap: 32px;
          margin-bottom: 24px;
        }

        .seuil-amount,
        .seuil-date {
          font-size: 16px;
          color: #64748b;
          margin: 0;
        }

        .seuil-amount strong,
        .seuil-date strong {
          color: #1e293b;
          font-weight: 600;
        }

        .seuil-progress {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: #f1f5f9;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(135deg, #10b981, #059669);
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 14px;
          font-weight: 600;
          color: #10b981;
        }

        /* Charts Section */
        .charts-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(600px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .chart-card {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .chart-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
        }

        .chart-header {
          padding: 24px 28px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.03) 0%, rgba(0, 138, 68, 0.03) 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .chart-title {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .chart-content {
          padding: 28px;
        }

        /* Commentaires Section */
        .commentaires-section {
          margin-bottom: 32px;
        }

        .commentaires-card {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .commentaires-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 28px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(0, 138, 68, 0.05) 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .commentaires-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .commentaires-date {
          font-size: 14px;
          color: #64748b;
          font-weight: 500;
        }

        .commentaires-content {
          padding: 28px;
        }

        .commentaire-item {
          margin-bottom: 24px;
        }

        .commentaire-item:last-child {
          margin-bottom: 0;
        }

        .commentaire-title {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 12px 0;
          padding-left: 12px;
          position: relative;
        }

        .commentaire-title::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 16px;
          background: linear-gradient(135deg, #dc3545, #008A44);
          border-radius: 2px;
        }

        .commentaire-text {
          font-size: 15px;
          line-height: 1.6;
          color: #475569;
          margin: 0;
          padding-left: 12px;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .main-wrapper {
            margin-left: 0;
          }

          .charts-section {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 20px;
          }

          .kpis-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .kpi-card {
            padding: 20px;
          }

          .kpi-value {
            font-size: 24px;
          }

          .page-title {
            font-size: 28px;
          }

          .seuil-info {
            flex-direction: column;
            gap: 16px;
          }

          .seuil-content,
          .chart-content,
          .commentaires-content {
            padding: 20px;
          }

          .chart-header,
          .seuil-header,
          .commentaires-header {
            padding: 20px;
          }
        }

        @media (max-width: 480px) {
          .main-content {
            padding: 16px;
          }

          .kpi-content {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }

          .kpi-icon {
            align-self: center;
          }

          .page-title {
            font-size: 24px;
          }

          .chart-content {
            padding: 16px;
          }

          .commentaires-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }

        /* Animation d'entrée */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .kpi-card,
        .chart-card,
        .seuil-card,
        .commentaires-card {
          animation: fadeInUp 0.6s ease-out;
        }

        /* Amélioration des tooltips des graphiques */
        .nivo-tooltip {
          background: #ffffff !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
          border-radius: 8px !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
          font-size: 13px !important;
          font-weight: 500 !important;
        }

        /* Styles pour les graphiques Nivo */
        .nivo-line text,
        .nivo-bar text,
        .nivo-pie text {
          font-family: 'Inter', sans-serif !important;
          font-size: 12px !important;
          font-weight: 500 !important;
        }

        .nivo-line .tick line,
        .nivo-bar .tick line {
          stroke: #e2e8f0 !important;
        }

        .nivo-line .grid line,
        .nivo-bar .grid line {
          stroke: #f1f5f9 !important;
          stroke-width: 1px !important;
        }

        /* Amélioration de la lisibilité sur les petits écrans */
        @media (max-width: 600px) {
          .chart-content {
            padding: 12px;
          }
          
          .chart-content > div {
            height: 300px !important;
          }
        }
            `}</style>
        </>
    );
};
export default Perfomancly ;